import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { MultiValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectMulti } from '@/components/element/Form/SelectMulti';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useWorkspaceUsers } from '@/hooks/api/0150_workspace_users';
import { useBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useProducts } from '@/hooks/api/0390_products';
import { useSalesContexts } from '@/hooks/api/2130_sales_contexts';
import { useMenuListedTeams } from '@/hooks/api/2260_menu_listed_teams';
import {
  useDashboardSearchCondition,
  WeeklyComparisonDashboardSearchCondition,
} from '@/providers/DashboardSearchConditionProvider';

export const FilterModal = () => {
  const {
    weeklyComparisonDashboardSearchCondition: searchCondition,
    setWeeklyComparisonDashboardSearchCondition: setSearchCondition,
  } = useDashboardSearchCondition();
  const [condition, setCondition] = useState<WeeklyComparisonDashboardSearchCondition>({ ...searchCondition });

  useEffect(() => setCondition({ ...searchCondition }), [searchCondition]);

  const onTeamsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, team_ids: d.map((a) => a.value) }));
  };

  const onUsersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, user_ids: d.map((a) => a.value) }));
  };

  const onProductsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, product_ids: d.map((a) => a.value) }));
  };

  const onSalesContextsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, sales_context_ids: d.map((a) => a.value) }));
  };

  const onBusinessPartnersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, business_partner_ids: d.map((a) => a.value) }));
  };

  const onAgenciesChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, agency_ids: d.map((a) => a.value) }));
  };

  const onApplyClick = () => {
    setSearchCondition({ ...condition });
  };

  return (
    <div
      className="modal fade"
      id="modal-weekly-comparison-filter"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="modal-form"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md" role="document">
        <div className="modal-content">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Filters
              </h5>
              <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <label>チーム</label>
                  <TeamsSelect onChange={onTeamsChange} selectedIds={condition.team_ids} />
                </div>
                <div className="form-group">
                  <label>売上カテゴリ</label>
                  <ProductsSelect onChange={onProductsChange} selectedIds={condition.product_ids} />
                </div>
                <div className="form-group">
                  <label>担当者</label>
                  <UsersSelect onChange={onUsersChange} selectedIds={condition.user_ids} />
                </div>
                <div className="form-group">
                  <label>取引先</label>
                  <BusinessPartnersSelect
                    onChange={onBusinessPartnersChange}
                    selectedIds={condition.business_partner_ids}
                  />
                </div>
                <div className="form-group">
                  <label>代理店</label>
                  <BusinessPartnersSelect onChange={onAgenciesChange} selectedIds={condition.agency_ids} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                Close
              </button>
              <button type="button" className="btn bg-gradient-primary" data-bs-dismiss="modal" onClick={onApplyClick}>
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UsersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useWorkspaceUsers({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.user_account.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const TeamsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useMenuListedTeams({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.sales_order_teams.map((d): SelectOptionType => ({ value: d.id, label: d.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const ProductsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useProducts({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const SalesContextsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useSalesContexts();
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.label })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const BusinessPartnersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data: businessPartnersData } = useBusinessPartners({
    identifier: _w_!.identifier,
    display_type: 'sales',
    page: 1,
    per: 1000,
  });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (businessPartnersData)
      setOptions(
        businessPartnersData.business_partners.map((d): SelectOptionType => ({ value: d.id, label: d.display_name }))
      );
  }, [businessPartnersData]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};
